import { forwardRef, type ReactNode } from "react";
import type { Caixa } from "@bookdental/schemas";
import { cn } from "@/lib/cn";

/** Posição de uma peça em porcentagem da grade — a planta acompanha a largura da tela. */
export const posicao = (c: Caixa, colunas: number, linhas: number) => ({
  left: `${(c.x / colunas) * 100}%`,
  top: `${(c.y / linhas) * 100}%`,
  width: `${(c.w / colunas) * 100}%`,
  height: `${(c.h / linhas) * 100}%`,
});

type Props = {
  colunas: number;
  linhas: number;
  comLinhas?: boolean;
  /** Teto da altura (CSS): planta alta e estreita encolhe na largura em vez de passar da tela. */
  alturaMaxima?: string;
  className?: string;
  children: ReactNode;
};

/** A grade da planta: proporção fixa (colunas × linhas) e linhas de célula ao fundo, se `comLinhas`. */
export const GradeDaPlanta = forwardRef<HTMLDivElement, Props>(
  function GradeDaPlanta({ colunas, linhas, comLinhas = false, alturaMaxima = "75vh", className, children }, ref) {
    return (
      <div
        ref={ref}
        className={cn("relative mx-auto select-none overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50 dark:border-border dark:bg-muted/20", className)}
        style={{ aspectRatio: `${colunas} / ${linhas}`, width: `min(100%, calc(${alturaMaxima} * ${colunas / linhas}))` }}
      >
        {comLinhas && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full text-zinc-200 dark:text-border" aria-hidden>
            {Array.from({ length: colunas - 1 }, (_, i) => (
              <line key={`c${i}`} x1={`${((i + 1) / colunas) * 100}%`} x2={`${((i + 1) / colunas) * 100}%`} y1="0" y2="100%" stroke="currentColor" strokeWidth={1} />
            ))}
            {Array.from({ length: linhas - 1 }, (_, i) => (
              <line key={`l${i}`} y1={`${((i + 1) / linhas) * 100}%`} y2={`${((i + 1) / linhas) * 100}%`} x1="0" x2="100%" stroke="currentColor" strokeWidth={1} />
            ))}
          </svg>
        )}
        {children}
      </div>
    );
  },
);
